import React, { ComponentProps, FC, useMemo, useState } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import ProjectPageLayout from '@adsk/alloy-project-page-layout';
import { TOOL_IDS } from '@adsk/alloy-provider';
import SideNavigation from '@adsk/alloy-side-navigation';

import AlloyContextSubscriber from './AlloyContextSubscriber';
import { ConnectedAnnouncementBanner } from './AnnouncementBanner/ConnectedAnnouncementBanner';
import { useFeatureFlag } from './LaunchDarklyProvider';
import LocalSideNavigation from './LocalSideNavigation/LocalSideNavigation';
import { PageNotFoundRedirect } from './PageNotFoundRedirect/PageNotFoundRedirect';
import RemoteNetworkBuildersProfileModal from './RemoteNetworkApiComponents/RemoteNetworkBuildersProfileModal';
import RemoteNetworkOnboardingACCUserModal from './RemoteNetworkApiComponents/RemoteNetworkOnboardingACCUserModal';
import { ToolRoute } from '../envs/types';
import getCurrentRoute from '../utils/getCurrentRoute';

const RootProjectPageLayout: FC = () => {
  const location = useLocation();
  const currentRoute = getCurrentRoute<ToolRoute>(location);

  const [isBuildersProfileModalEnabled, setIsBuildersProfileModalEnabled] =
    useState<boolean | undefined>(false);
  const [isBuildersProfileModalShown, setIsBuildersProfileModalShown] =
    useState(false);

  const { value: useAccNetworkApiComponentsRemote } = useFeatureFlag<boolean>(
    'useAccNetworkApiComponentsRemote',
    false,
  );

  const productId = useMemo(() => {
    if (currentRoute?.layoutProps?.dynamicProductIdSetter) {
      return (
        currentRoute?.layoutProps?.dynamicProductIdSetter?.({ location }) ||
        currentRoute?.layoutProps?.currentProductId
      );
    }

    return currentRoute?.layoutProps?.currentProductId;
  }, [currentRoute, location]);

  if (!currentRoute) {
    return <PageNotFoundRedirect />;
  }

  const { layoutProps } = currentRoute;

  return (
    <ProjectPageLayout
      isUsersConstructionApi
      renderAboveLayout={() => <ConnectedAnnouncementBanner />}
      renderSideNavigation={(props: ComponentProps<typeof SideNavigation>) => (
        <LocalSideNavigation
          {...props}
          selectedId={layoutProps.currentToolId}
        />
      )}
      {...layoutProps}
      currentProductId={productId}
      currentToolId={
        layoutProps.currentToolId === 'demo-app'
          ? TOOL_IDS.RFIS
          : layoutProps.currentToolId
      }
      UNSAFE_showBuildersUserProfileModal={() => {
        setIsBuildersProfileModalShown(true);
      }}
      UNSAFE_isBuildersProfileModalEnabled={isBuildersProfileModalEnabled}
    >
      <AlloyContextSubscriber>
        {useAccNetworkApiComponentsRemote && (
          <>
            <RemoteNetworkOnboardingACCUserModal
              showModal={true}
              onSuccess={() => {
                setIsBuildersProfileModalEnabled(undefined);
              }}
            />
            <RemoteNetworkBuildersProfileModal
              setShowModal={setIsBuildersProfileModalShown}
              showModal={isBuildersProfileModalShown}
              isBuildersProfileModalEnabled={isBuildersProfileModalEnabled}
              setIsBuildersProfileModalEnabled={
                setIsBuildersProfileModalEnabled
              }
            />
          </>
        )}
        <Outlet />
      </AlloyContextSubscriber>
    </ProjectPageLayout>
  );
};

export default RootProjectPageLayout;
